import { cn } from '@ludus/ui'
import type { PointerEvent as ReactPointerEvent } from 'react'
import type { ManagerSchedule, ScheduleWindow, Weekday } from '../../types.ts'
import { ScheduleBlock } from './ScheduleBlock.tsx'
import { HOUR_HEIGHT, TOTAL_HEIGHT } from './timeMath.ts'
import type { DragPreview } from './useDragSelect.ts'

interface DayColumnProps {
  date: string
  weekday: Weekday
  isToday: boolean
  schedule: ManagerSchedule | null
  managerColor: string
  preview: DragPreview | null
  onPointerDown: (e: ReactPointerEvent<HTMLDivElement>, date: string) => void
  onTemplateClick: (weekday: Weekday, index: number, window: ScheduleWindow) => void
  onExceptionClick: (date: string, index: number, window: ScheduleWindow) => void
  onOffDayClick: (date: string) => void
  className?: string
}

export function DayColumn({
  date,
  weekday,
  isToday,
  schedule,
  managerColor,
  preview,
  onPointerDown,
  onTemplateClick,
  onExceptionClick,
  onOffDayClick,
  className,
}: DayColumnProps) {
  const dayException = schedule?.exceptions.find((e) => e.date === date) ?? null
  const isOffDay =
    dayException !== null && (dayException.windows === null || dayException.windows.length === 0)
  const templateDay = schedule?.weeklyTemplate.find((d) => d.weekday === weekday) ?? null
  const showTemplate = !dayException
  const previewForThisDay = preview?.date === date ? preview : null

  return (
    <div
      data-date={date}
      onPointerDown={(e) => onPointerDown(e, date)}
      className={cn(
        'relative flex-1 cursor-cell select-none border-r border-border/60 last:border-r-0',
        isToday && 'bg-primary-light/30',
        className,
      )}
      style={{ height: TOTAL_HEIGHT }}
    >
      {Array.from({ length: 24 }, (_, hour) => (
        <div
          key={hour}
          className="absolute left-0 right-0 border-t border-border/30 first:border-t-0"
          style={{ top: hour * HOUR_HEIGHT, height: HOUR_HEIGHT }}
        />
      ))}

      {isOffDay && (
        <ScheduleBlock
          kind="off"
          startMinute={0}
          endMinute={1440}
          label="Off-day"
          onClick={() => onOffDayClick(date)}
        />
      )}

      {showTemplate &&
        templateDay?.windows.map((w, i) => (
          <ScheduleBlock
            key={`tpl-${i}`}
            kind="template"
            startMinute={w.startMinute}
            endMinute={w.endMinute}
            color={managerColor}
            onClick={() => onTemplateClick(weekday, i, w)}
          />
        ))}

      {dayException &&
        dayException.windows &&
        dayException.windows.map((w, i) => (
          <ScheduleBlock
            key={`exc-${i}`}
            kind="exception"
            startMinute={w.startMinute}
            endMinute={w.endMinute}
            color={managerColor}
            onClick={() => onExceptionClick(date, i, w)}
          />
        ))}

      {previewForThisDay && (
        <ScheduleBlock
          kind="preview"
          startMinute={previewForThisDay.startMinute}
          endMinute={previewForThisDay.endMinute}
        />
      )}
    </div>
  )
}
